import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence, useTransform, useScroll } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { RevealLines, Overline } from "./Motion";
import { IMAGES } from "@/lib/site";

const SLIDES = [
  { src: IMAGES.woodTexture, label: "Engineered Wood", tag: "Flooring" },
  { src: "/products/ipe-deck.png", label: "Ipe Deck Flooring", tag: "Outdoor" },
  { src: "/products/sports-flooring.png", label: "Sports Flooring", tag: "Performance" },
  { src: "/products/terracotta-facade.png", label: "Terracotta Facade", tag: "Facade" },
  { src: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d", label: "Solid Hardwood", tag: "Flooring" },
];

// Reliable auto-cycling image showcase with crossfade + slow zoom.
const HeroShowcase = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 3800);
    return () => clearInterval(timer.current);
  }, [paused]);

  const active = SLIDES[index];

  return (
    <div
      data-testid="hero-3d-scene"
      className="relative h-[420px] sm:h-[520px] lg:h-[640px] w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Main framed image */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
        className="absolute inset-x-0 top-0 h-[78%] rounded-xl overflow-hidden ring-1 ring-black/10"
        style={{ boxShadow: "0 50px 90px -30px rgba(11,21,84,0.5)" }}
      >
        <AnimatePresence mode="sync">
          <motion.img
            key={index}
            src={active.src}
            alt={`${active.label} — premium ${active.tag.toLowerCase()} by Fascinated by Floors`}
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 1, scale: 1.04 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{ opacity: { duration: 1 }, scale: { duration: 4.2, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
            draggable="false"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-royal-deep/70 via-transparent to-transparent" />

        {/* caption */}
        <div className="absolute bottom-5 left-5 text-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-[11px] tracking-[0.22em] uppercase text-white/70">{active.tag}</span>
              <div className="font-display text-2xl lg:text-3xl leading-tight">{active.label}</div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Thumbnails */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.7 }}
        className="absolute bottom-0 inset-x-0 flex items-center gap-3"
      >
        {SLIDES.map((s, i) => (
          <button
            key={i}
            data-testid={`hero-thumb-${i}`}
            onClick={() => setIndex(i)}
            aria-label={s.label}
            className={`relative h-16 sm:h-[76px] flex-1 rounded-lg overflow-hidden ring-1 transition-all duration-500 ${
              i === index ? "ring-2 ring-royal scale-[1.02]" : "ring-black/10 opacity-60 hover:opacity-100"
            }`}
          >
            <img src={s.src} alt={s.label} className="h-full w-full object-cover" draggable="false" />
            {i === index && (
              <motion.span
                layoutId="thumb-active"
                className="absolute inset-x-0 bottom-0 h-1 bg-royal"
              />
            )}
          </button>
        ))}
      </motion.div>

      {/* floating badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        className="absolute top-6 right-4 sm:right-6 bg-white/85 backdrop-blur-xl border border-black/5 rounded-2xl px-5 py-4 shadow-xl"
      >
        <div className="font-display text-3xl text-royal leading-none">40+</div>
        <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mt-1">Global mills</div>
      </motion.div>
    </div>
  );
};

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen bg-bone overflow-hidden">
      {/* faint oversized watermark */}
      <div className="pointer-events-none absolute -right-10 top-24 font-display text-[24vw] leading-none text-royal/[0.04] select-none hidden md:block">
        Floors
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-36 lg:pt-44 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-6 relative z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <Overline>Flooring · Ceiling · Facade</Overline>
          </motion.div>

          <h1 className="mt-6 font-display font-semibold text-royal-deep text-5xl sm:text-6xl lg:text-[6rem] tracking-tighter leading-[0.92]">
            <RevealLines lines={["Beyond Floors.", "Beyond"]} testid="hero-heading" />
            <span className="reveal-mask">
              <motion.span
                className="block italic text-royal"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.24 }}
              >
                Expectations.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-base lg:text-lg text-muted-foreground leading-relaxed"
          >
            We represent the world&rsquo;s finest manufacturers of flooring, ceiling and terracotta
            facade systems — curated for architects, builders and those who are, like us,
            fascinated by floors.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={onQuote}
              className="group inline-flex items-center gap-2 bg-royal text-white text-sm font-medium pl-6 pr-5 py-3.5 rounded-full hover:bg-royal-dark transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-explore-btn"
              onClick={() => document.querySelector("#verticals")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 text-sm font-medium text-royal-deep border-b-2 border-transparent hover:border-royal transition-colors duration-300 pb-1"
            >
              Explore our verticals <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative z-0">
          <HeroShowcase />
        </div>
      </div>
    </section>
  );
};
